/**
 * Research progress for the whole signed-in dashboard: the research stream,
 * and a background poll of the requests this tab started.
 *
 * Why at the shell and not on the page that asked: a reader who starts
 * "Identify" on the transactions page and opens one of the addresses expects
 * that address page to fill in when the research lands. The page that asked is
 * gone by then; this is not. Whatever hears a subject finish — a push here, the
 * background poll here, or a host's own list poll — refreshes exactly that
 * address's queries (`invalidateAddressQueries`), and the shared tracker makes
 * sure only the FIRST of them does.
 *
 * - **Stream** — subscribed only when the research stream is known to this
 *   build (`researchStreamKnown`), the plan has server streaming
 *   (`useServerStreaming`) and a client is mounted. `live` is true while its
 *   channels are open, and hosts relax their polling on it.
 * - **Background poll** — the fallback that makes everything work without the
 *   stream: requests this tab started (or saw running) that nobody has heard
 *   from for {@link BACKGROUND_POLL_MS} are read by id. A host listing the same
 *   request keeps it fresh, so the two never both ask.
 *
 * Mounted inside `AuthenticatedStreaming`, which is keyed by identity, so the
 * tracker and every subscription are one identity's and go with it.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import {
  STREAM_RESEARCH,
  connectionStateForChannels,
  ConnectionStatus,
  useOptionalStreamingClient,
} from "@authe/streaming";
import { createApiResearchSource, type ResearchRequest } from "@/api/researchSource";
import { useCallerChannels } from "@/app/streamChannels";
import { useServerStreaming } from "@/app/serverStreaming";
import { invalidateAddressQueries } from "@/features/overlays/invalidateAddressQueries";
import {
  BACKGROUND_BATCH,
  BACKGROUND_POLL_MS,
  BACKGROUND_SAFETY_MS,
  ResearchLiveContext,
  researchStreamKnown,
  type ResearchLive,
  type ResearchUpdateListener,
  type ResearchUpdateSource,
} from "./researchLive";
import {
  ResearchProgressTracker,
  readResearchEvent,
  researchUpdateFromRequest,
  type ResearchUpdate,
} from "./researchProgress";

export function ResearchLiveProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const queryClient = useQueryClient();
  const api = useApiClient();
  const research = React.useMemo(() => createApiResearchSource(api), [api]);
  const client = useOptionalStreamingClient();
  const { available } = useServerStreaming();
  const [tracker] = React.useState(() => new ResearchProgressTracker());
  const listeners = React.useRef(new Set<ResearchUpdateListener>());
  const known = researchStreamKnown();
  const channels = useCallerChannels(STREAM_RESEARCH);
  const streaming = Boolean(client && available && known && channels.length > 0);

  const hear = React.useCallback(
    (update: ResearchUpdate, source: ResearchUpdateSource, request: ResearchRequest | null) => {
      invalidateAddressQueries({ queryClient, api }, tracker.observe(update, source));
      for (const listener of listeners.current) listener(update, source, request);
    },
    [queryClient, api, tracker],
  );

  // The stream: every audience the caller hears research on.
  React.useEffect(() => {
    if (!streaming || !client) return;
    const offs = channels.map((channel) =>
      client.subscribeChannel(channel, (message) => {
        const update = readResearchEvent(message.payload);
        if (update) hear(update, "stream", null);
      }),
    );
    return () => {
      for (const off of offs) off();
    };
  }, [streaming, client, channels, hear]);

  // Whether those channels are open — the one thing hosts relax their polling on.
  const [live, setLive] = React.useState(false);
  React.useEffect(() => {
    if (!streaming || !client) {
      setLive(false);
      return;
    }
    const ids = channels.map((channel) => channel.id);
    const read = () => setLive(connectionStateForChannels(client.getState(), ids).status === ConnectionStatus.OPEN);
    read();
    return client.onState(read);
  }, [streaming, client, channels]);

  // The background poll of tracked requests nobody has heard from lately.
  React.useEffect(() => {
    const every = live ? BACKGROUND_SAFETY_MS : BACKGROUND_POLL_MS;
    let cancelled = false;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      const stale = tracker.staleActive(every).slice(0, BACKGROUND_BATCH);
      for (const id of stale) {
        void research.get(id, { signal: controller.signal }).then(
          (request) => {
            if (!cancelled) hear(researchUpdateFromRequest(request), "poll", request);
          },
          () => {
            /* One failed read is not the end: the next tick asks again. */
          },
        );
      }
    }, every);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [live, tracker, research, hear]);

  const value = React.useMemo<ResearchLive>(
    () => ({
      tracker,
      live,
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
      track: (request) => {
        const update = researchUpdateFromRequest(request);
        tracker.track(update);
        // Hosts listing this context hear of it at once — a reader's own
        // selections panel on the page they asked from, say.
        for (const listener of listeners.current) listener(update, "poll", request);
      },
    }),
    [tracker, live],
  );
  return <ResearchLiveContext.Provider value={value}>{children}</ResearchLiveContext.Provider>;
}
